// ---------------------------------------------------------------------------
// Tunables. Scoring and timing live here so house rules stay possible without
// touching the engine.
// ---------------------------------------------------------------------------
// --- Letters ---------------------------------------------------------------
export const LETTERS_TILE_COUNT = 9;
export const LETTERS_MIN_VOWELS = 3;
export const LETTERS_MIN_CONSONANTS = 4;
/**
 * Tile distributions for the two piles, drawn without replacement.
 *
 * These weights are what make boards playable. A uniform draw over 21
 * consonants produces unusable letters; this makes R roughly 12% likely and Q
 * roughly 1.4%.
 *
 * Source: the distribution published for the Channel 4 series, as reproduced on
 * the apterous.org tile reference and incoherency.co.uk's letter-frequency
 * page. Verified letter by letter against both on 2026-07-30: vowels
 * A15 E21 I13 O13 U5 = 67, and the 21 consonants = 74. Both totals and every
 * individual count match, so the earlier VERIFY note is discharged.
 */
export const VOWEL_PILE = {
    A: 15,
    E: 21,
    I: 13,
    O: 13,
    U: 5,
};
export const CONSONANT_PILE = {
    B: 2,
    C: 3,
    D: 6,
    F: 2,
    G: 3,
    H: 2,
    J: 1,
    K: 1,
    L: 5,
    M: 4,
    N: 8,
    P: 4,
    Q: 1,
    R: 9,
    S: 9,
    T: 9,
    V: 1,
    W: 1,
    X: 1,
    Y: 1,
    Z: 1,
};
export const VOWEL_PILE_SIZE = Object.values(VOWEL_PILE).reduce((a, b) => a + b, 0);
export const CONSONANT_PILE_SIZE = Object.values(CONSONANT_PILE).reduce((a, b) => a + b, 0);
// --- Numbers ---------------------------------------------------------------
export const NUMBERS_TILE_COUNT = 6;
export const NUMBERS_LARGE_TILES = [25, 50, 75, 100];
/** Two sets of 1 to 10. */
export const NUMBERS_SMALL_TILES = Array.from({ length: 20 }, (_, i) => (i % 10) + 1);
export const NUMBERS_MIN_LARGE = 0;
export const NUMBERS_MAX_LARGE = 4;
export const NUMBERS_TARGET_MIN = 101;
export const NUMBERS_TARGET_MAX = 999;
/** Five steps is the most six tiles can support. */
export const NUMBERS_MAX_STEPS = 5;
/**
 * How long the board waits between putting up one number and the next.
 *
 * The numbers round used to arrive complete: one socket frame carried the large
 * count, all six tiles and the target, and the room went from "two large
 * please" to a finished board between frames. The letters round has never
 * worked that way — the board being built one tile at a time is the show — and
 * the reason numbers did was that the chooser makes ONE decision there instead
 * of nine, so there was nothing to pace it against.
 *
 * Longer than a letters tile's own `TILE_STAGGER_MS` on purpose. There are six
 * of these rather than nine and nobody is pressing anything between them, so
 * the row is a thing being watched rather than a thing being driven, and it can
 * afford to breathe.
 */
export const NUMBERS_DEAL_MS = 620;
/**
 * The pause between the last tile and the target landing.
 *
 * Spent, on the client, spinning through three-digit numbers that mean nothing.
 * The server sends the real target when this expires and not one moment before
 * — the spin is the client's own `Math.random` over a window whose LENGTH is
 * the only thing the server decides, so there is nothing on the wire to read
 * ahead and nothing in the state to leak. See DESIGN §6.3.
 *
 * It is deliberately the longest beat in the round. It is the only moment in a
 * numbers game where everybody in the room is waiting on the same thing.
 */
export const NUMBERS_TARGET_SPIN_MS = 2_400;
/**
 * How long the settled target holds the screen before the board takes over.
 *
 * The target used to land and the play screen replaced it in the same frame,
 * so the number everybody had just spent two and a half seconds waiting for was
 * gone before it had been read. The payoff of a countdown is the moment it
 * stops, and it needs somewhere to land.
 *
 * Deliberately not longer. This is time added to every numbers round in the
 * game, spent watching a number that is about to be at the top of the next
 * screen anyway, so it buys the beat and nothing more.
 */
export const NUMBERS_TARGET_HOLD_MS = 900;
// --- Scoring ---------------------------------------------------------------
/** Letters score the length of the word, so a nine scores 18. */
export const LETTERS_NINE_BONUS_MULTIPLIER = 2;
export const NUMBERS_SCORING = [
    { withinInclusive: 0, points: 10 },
    { withinInclusive: 5, points: 7 },
    { withinInclusive: 10, points: 5 },
];
export const CONUNDRUM_POINTS = 10;
// --- Timing ---------------------------------------------------------------
export const HEARTBEAT_INTERVAL_MS = 1_000;
/**
 * Grace beyond the last heartbeat. A player's effective deadline is
 * min(round deadline, lastHeartbeat + this), which closes the pull-the-cable
 * exploit while costing an ordinary wifi blip nothing.
 */
export const HEARTBEAT_GRACE_MS = 1_500;
export const DRAFT_DEBOUNCE_MS = 300;
export const RECONNECT_BACKOFF_START_MS = 250;
/** Host passes to the longest-connected player after this much verified downtime. */
export const HOST_TRANSFER_AFTER_MS = 60_000;
/** Visible countdown after a restart, so no clock moves against a dead screen. */
export const RESUME_COUNTDOWN_MS = 3_000;
// A `REVEAL_STEP_MS` was here: a client-side animation step inside a single
// reveal entry, kept after the engine took over the pacing because the "how long
// does a game take" estimate still read it. That estimate was rewritten against
// the engine's own numbers, which left the constant with no callers and a
// comment claiming a reader it no longer had. The pacing between entries is
// `revealHoldMs` below, and it is the engine's, not the client's.
//
// **Every number the reveal runs on is now in this file**, the closing slides
// included — they were `PAYOFF_LEAD_MS` and `PAYOFF_BEAT_MS` in the client until
// 2026-08-01, which meant the room's pace control could not reach them. See
// `REVEAL_PAYOFF_BEAT_MS`.
/**
 * The base hold for one reveal entry: `REVEAL_ENTRY_MS` for a dash, plus
 * `REVEAL_ANSWER_EXTRA_MS` when there is an actual answer to read.
 *
 * The reveal is the part of the show people watch, and it used to arrive as one
 * burst of events the client raced through in a second. Pacing it in the engine
 * fixed the racing; the first numbers were still too quick to watch, and the
 * report from the room was that the entries "go by way too fast".
 *
 * **Slowed 2026-07-31.** Three seconds is about how long it takes to read a name
 * and register that somebody has nothing, and an entry with an actual answer in
 * it needs nearly twice that: the word appears, the room reads it, the verdict
 * and the score land after it. The gap between the two is deliberately wide now
 * rather than the single second it was — a dash is the one entry nobody needs
 * time for, and spending it there is what made the answers feel rushed.
 *
 * **Retimed 2026-08-01, after the room watched it with the timings above.** The
 * report was three separate things and only one of them was a number being too
 * small: "hangs a little long on the first person's reveal, too fast on the 2nd
 * reveal, too fast past the best available word."
 *
 * The first and the second were the same defect seen from both ends. The lead
 * sits in front of entry one and nothing else, so the opening beat was a blank
 * frame plus a full entry while every other entry was just a full entry — and
 * the *last* entry had no hold at all, because the release that put it out and
 * the cursor reaching the end happened on the same tick, so the payoff replaced
 * it a client beat later. In a two-player round that is entry one hanging and
 * entry two flashing past, which is exactly what came back. The queue ascends,
 * so the entry with no hold was always the best answer in the round.
 *
 * The fix for the second is structural and lives in `gameEngine.ts`: the reveal
 * now runs one step past the end of its queue, and that step is the last
 * answer's own hold. Every entry gets the same beat, no exceptions.
 *
 * **Lowered again 2026-08-01, later the same day, and the flat beat went with
 * them.** At 2.4s and +3.6s an answer held six seconds flat: eight answers was
 * about fifty seconds and sixteen would have been ninety-six, which is not a
 * reveal anybody sits through. The report was that it "hangs too long per word",
 * and that a full room would be unwatchable.
 *
 * These two are now the BASE, not the answer. What one entry actually gets is
 * `revealHoldMs` below, which weights the base by where the entry sits in the
 * queue and by how many entries there are. A small round still plays at the
 * base; a big one compresses toward `REVEAL_FLOOR_MS`, most at the front and
 * least at the back.
 *
 * **How much most and least is was retuned 2026-08-03**, because the answer at
 * the time — front on the floor, back on the full base — was a reveal that
 * flickered and then stopped. See `REVEAL_TAIL_DECAY`.
 *
 * The host can hurry any of it along, hold it (see `set-reveal-paused`), and
 * change the base for the whole room with `revealPacePercent`.
 * `REVEAL_AUTO_ADVANCE_MS` is untouched.
 */
export const REVEAL_ENTRY_MS = 1_800;
export const REVEAL_ANSWER_EXTRA_MS = 2_400;
/**
 * The readability floor. No entry holds for less than this, whatever the
 * compression works out to, and it is the one number the budget may not win a
 * fight with.
 *
 * A hard total was offered and turned down for exactly this reason: fifteen
 * seconds across sixteen answers is half a second each, which is a flash rather
 * than something a room can read. Sixteen answers lands at about twenty-seven
 * seconds instead — over the budget, under the old ninety-six, and legible the
 * whole way down.
 *
 * **Nothing has stood on it since 2026-08-03.** The flattened spread puts the
 * front of a full queue at about 1.1s, so the floor is a floor again rather than
 * the thing four fifths of a crowded round was pinned to. It is still the one
 * number the budget may not win a fight with.
 */
export const REVEAL_FLOOR_MS = 900;
/**
 * Extra time on the last entry, always.
 *
 * The queue is worst-first, so the last entry is the best answer in the round.
 * It is the one everybody wants to look at, it is what the board's best word is
 * about to be judged against, and it is the one the reveal used to cut short.
 * This is on top of whatever the curve gives it and it is never compressed away,
 * which is what keeps the last entry the longest at every size: a four-answer
 * round runs 4.2 / 4.2 / 4.2 / 5.5.
 *
 * **What it no longer does is make the last entry a different order of thing.**
 * Until 2026-08-03 the curve handed the last entry the full base as well, so a
 * sixteen-answer round gave it 5.5s against a front of 0.91 — six to one, on a
 * queue where the interesting difference between two answers is a letter. It
 * closes on 3.62s now and the round still plainly builds to it. The bonus is a
 * beat for the best word, not a budget for it.
 */
export const REVEAL_TAIL_BONUS_MS = 1_300;
/**
 * Where compression starts, and how sharply it bites.
 *
 * Up to `REVEAL_COMPRESS_FROM` answers every entry plays at its full base, and
 * that number is four rather than six for a reason given below. Past it the
 * weight of an entry falls off with how far it is from the end of the queue,
 * reaching full compression at `REVEAL_COMPRESS_TO`.
 *
 * **`REVEAL_COMPRESS_TO` is sixteen and it is not a seat limit**, though it was
 * one until the cap was removed: this curve read `MAX_PLAYERS_CAP`, and the two
 * numbers agreeing was a coincidence of the palette rather than a fact about
 * pacing. Sixteen is where this curve bottoms out because that is what it was
 * solved for and what `gameEngine.test.ts` pins at every size from 1 to 16. A
 * queue longer than that clamps here and plays at full compression; a round that
 * would actually queue five hundred answers does not reach this function at all,
 * because `aggregateReveal` has turned it into a different screen by then.
 *
 * `REVEAL_COMPRESS_CURVE` is how fast the compression itself arrives —
 * `1 - x^3.4` where `x` is how far the round is from a full one — so it bites
 * early and then eases, rather than waiting and then lurching.
 *
 * **The two decays below are a budget and a shape, and they were one number
 * until 2026-08-03.** `REVEAL_BUDGET_DECAY` says how much a queue of a given
 * size is allowed to spend in total; `REVEAL_TAIL_DECAY` says how that is spread
 * across it. Splitting them is what let the spread be retuned without moving a
 * single total — see `revealHoldMs`.
 *
 * **All three are solved against one constraint that is easy to miss: the total
 * has to be non-decreasing in the number of answers.**
 *
 * The first cut of this curve was not, and it was a real defect rather than a
 * curiosity. It held compression off until six answers and then applied it hard,
 * which put eight answers at about 31s and sixteen at about 22s — so a room of
 * eight sat through ten seconds MORE reveal than a full room did. Nobody reads
 * that as a curve with two hand-set endpoints. They read it as the reveal being
 * slow in ordinary games and oddly brisk in big ones, which is the opposite of
 * the complaint that started the retiming.
 *
 * The fix was to unpin the top end. Sixteen answers is not squeezed into 20s any
 * more; the total climbs the whole way and settles just under 27s, which is
 * still a quarter of the ~96s the flat six-second entry would have cost. What
 * had to give was the budget, not the two things that were asked for by hand:
 * four answers still plays 4.2 / 4.2 / 4.2 / 5.5 exactly, and the floor is still
 * never divided into.
 *
 * Starting compression at four rather than six is what buys the monotonicity.
 * The ramp has to be gentle to keep the totals climbing, and a gentle ramp that
 * only starts at six has too little room left before the cap.
 */
export const REVEAL_COMPRESS_FROM = 4;
export const REVEAL_COMPRESS_TO = 16;
export const REVEAL_COMPRESS_CURVE = 3.4;
/**
 * The budget: how much a crowded queue spends in total.
 *
 * This is the 0.68 that used to be `REVEAL_TAIL_DECAY`, and it is kept to the
 * digit because every published total is its work — 19.0s at four answers,
 * 23.4s at eight, 26.9s at sixteen, climbing the whole way. What it means now is
 * only the AVERAGE weight a queue of a given size gets, never any one entry's.
 */
export const REVEAL_BUDGET_DECAY = 0.68;
/**
 * The shape: how that budget is spread across the queue.
 *
 * Still "closer to first place, the slower it gets" — each step back down the
 * queue keeps `0.88` of what the step after it kept, above the floor — but a
 * much gentler slope than the 0.68 it was until 2026-08-03.
 *
 * **Raised because the reveal was backloaded, not because it was long.** At 0.68
 * a sixteen-answer round put four fifths of its entries within a tenth of a
 * second of the floor and then spent half of the whole reveal on the last four
 * of them: 1.94 / 2.43 / 3.14 / 5.50 against a front that flickered past at
 * 0.91. Isaac's report was that "the last few reveals are really taking too
 * long", which is what an easing curve reads as from the room — the answers at
 * the front are not a reveal, they are a scroll, and then the screen stops
 * moving.
 *
 * 0.88 is deliberately short of flat. The queue is worst-first, so time really
 * should accumulate toward the end; what it may not do is hoard. Sixteen answers
 * now runs 1.11 up to 2.15 with the last on 3.62, a spread of about two to one
 * rather than the six to one it was.
 */
export const REVEAL_TAIL_DECAY = 0.88;
/**
 * The room's own hand on the pace, as a percentage of everything above.
 *
 * One control over the base rather than four over the parts: a room that wants
 * to linger sets 150 and every hold, the lead and the floor stretch together, so
 * the shape of the reveal is the same reveal played slower. 50 is double speed.
 *
 * The floor scales with it deliberately. It is a default for what a room can
 * read, not a safety rail: a host who has moved this slider down has said, in as
 * many words, that they want it faster than the default calls readable.
 */
export const REVEAL_PACE_MIN = 50;
export const REVEAL_PACE_MAX = 200;
export const REVEAL_PACE_DEFAULT = 100;
/**
 * How long one entry holds the screen.
 *
 * `index` is the entry's position in `revealOrder` and `count` is that queue's
 * length, so the function knows both how far from the payoff this entry is and
 * how crowded the round was. Pure, total, and the one implementation: the engine
 * paces the reveal with it and anything estimating how long a game takes has to
 * ask it rather than multiplying a constant by a player count.
 *
 * The shape, in one line each:
 *
 *   - base           a dash is cheap, an answer needs reading
 *   - compression    none at four answers or fewer, full at sixteen
 *   - budget         how much a queue of this size has to spend, in total
 *   - spread         how that is shared out along the queue
 *   - floor          nothing is ever shorter than `REVEAL_FLOOR_MS`
 *   - tail bonus     the last entry always gets the most
 *   - pace           the room's multiplier over all of it
 *
 * **Flattened 2026-08-03, and the totals did not move.** The old curve read the
 * decay as both the budget and the spread, so the only way to make a crowded
 * round less backloaded was to make it longer. Normalising the spread against
 * its own average separates the two: `REVEAL_BUDGET_DECAY` still sets what a
 * queue of a given size spends, and `REVEAL_TAIL_DECAY` now only decides which
 * end of it gets the money. Every total below is to the millisecond what it was.
 *
 * What it produces, in seconds, for a round where everybody answered:
 *
 *   |  n | first | second-last | last | total with the lead |
 *   | -- | ----- | ----------- | ---- | ------------------- |
 *   |  1 |  5.50 |      —      | 5.50 |  6.4                |
 *   |  4 |  4.20 |     4.20    | 5.50 | 19.0                |
 *   |  8 |  2.29 |     2.94    | 4.41 | 23.4                |
 *   | 12 |  1.35 |     2.32    | 3.80 | 23.9                |
 *   | 16 |  1.11 |     2.15    | 3.62 | 26.9                |
 *
 * Against what it produced before: sixteen answers used to run 0.91 at the front
 * and 1.94 / 2.43 / 3.14 / 5.50 across the last four, which is half the reveal
 * spent on a quarter of it. The last entry is still the longest at every size
 * and the first is still the shortest — this is more linear, not linear.
 *
 * The totals climb the whole way, which is the property the first cut of this
 * curve did not have. See `REVEAL_BUDGET_DECAY`.
 */
export function revealHoldMs(options) {
    const { index, count, hasAnswer } = options;
    const base = hasAnswer ? REVEAL_ENTRY_MS + REVEAL_ANSWER_EXTRA_MS : REVEAL_ENTRY_MS;
    // How far this entry is from the end of the queue. Distance from the END
    // rather than a fraction of the way through it, because the tail is what the
    // curve is anchored on: the last three entries of a sixteen-answer round get
    // a recognisable version of what the last three of an eight-answer one get,
    // and it is the stretch in front of them that is squeezed.
    const fromEnd = Math.max(0, count - 1 - index);
    // How far this round is from a full one, 1 at `REVEAL_COMPRESS_FROM` and 0 at
    // the cap. Raised to a power so compression arrives early and then eases: a
    // linear ramp leaves too much of the base in place at ten and twelve answers,
    // which is precisely what made the total dip between eight and sixteen.
    const span = REVEAL_COMPRESS_TO - REVEAL_COMPRESS_FROM;
    const room = Math.min(1, Math.max(0, (REVEAL_COMPRESS_TO - count) / span));
    const compression = 1 - room ** REVEAL_COMPRESS_CURVE;
    // The budget and the spread, and the ratio between their averages is the whole
    // of the flattening. `REVEAL_BUDGET_DECAY` decides what a queue of this length
    // gets on average — which is what every published total is — and dividing the
    // spread by its own average leaves it deciding nothing but the tilt. Raise
    // `REVEAL_TAIL_DECAY` and the front of the queue gains exactly what the back
    // gives up; the sum over the queue does not move at all.
    const budget = geometricMean(REVEAL_BUDGET_DECAY, count);
    const spread = REVEAL_TAIL_DECAY ** fromEnd / geometricMean(REVEAL_TAIL_DECAY, count);
    const weight = 1 - compression + compression * spread * budget;
    let ms = REVEAL_FLOOR_MS + (base - REVEAL_FLOOR_MS) * weight;
    if (fromEnd === 0)
        ms += REVEAL_TAIL_BONUS_MS;
    return Math.round(ms * paceFactor(options.pacePercent));
}
/**
 * The average of `decay^0 … decay^(count-1)`, in closed form.
 *
 * Both halves of `revealHoldMs`'s weight are one of these: the budget is the old
 * curve's own average, and the spread is divided by its average so that changing
 * the decay tilts the queue without spending anything extra on it.
 */
function geometricMean(decay, count) {
    if (count <= 1)
        return 1;
    return (1 - decay ** count) / (count * (1 - decay));
}
/** The lead before the first answer, at the room's pace. See `REVEAL_LEAD_MS`. */
export function revealLeadMs(pacePercent) {
    return Math.round(REVEAL_LEAD_MS * paceFactor(pacePercent));
}
function paceFactor(pacePercent) {
    const percent = typeof pacePercent === 'number' && Number.isFinite(pacePercent)
        ? Math.min(REVEAL_PACE_MAX, Math.max(REVEAL_PACE_MIN, pacePercent))
        : REVEAL_PACE_DEFAULT;
    return percent / 100;
}
/**
 * The beat before the first answer, after the declared lengths land.
 *
 * Deliberately shorter than an entry. It is a blank stage — the declared list
 * is up and no answer is out — and it stacks in front of the first entry's own
 * hold, so anything generous here reads as the reveal hanging before it starts.
 */
export const REVEAL_LEAD_MS = 900;
/**
 * The closing slides: the seam after the last answer, and the beat each slide
 * holds for.
 *
 * **These live here, not in the client, as of 2026-08-01.** They were
 * `PAYOFF_LEAD_MS` and `PAYOFF_BEAT_MS` in `frontend/src/lib/revealSequence.ts`,
 * which meant the reveal's timing was two files that had to be reasoned about
 * together and only one of them could be turned down by a room. The pace control
 * has to reach them: a host who slows the reveal and finds the payoff still
 * flashing past has not slowed the reveal.
 *
 * **The payoff gets MORE time, not less.** The rest of this retiming took time
 * away, and the one beat that was asked for explicitly is this one: "the board
 * had OUTRAGE" is the punchline of the round, it is a word nobody has seen
 * before this frame, and it was getting four seconds while an ordinary entry —
 * a name and a word the room watched somebody type — got six. It is six now, and
 * the compression above never touches it, because it is not an entry in a queue.
 *
 * The seam stays short. The stage has just held the last answer for its own full
 * hold plus the tail bonus, so a generous pause here is dead air on a screen
 * nobody is reading any more.
 */
export const REVEAL_PAYOFF_LEAD_MS = 1_200;
export const REVEAL_PAYOFF_BEAT_MS = 6_000;
/** The closing-slide beats at the room's pace. See `REVEAL_PAYOFF_BEAT_MS`. */
export function revealPayoffMs(pacePercent) {
    const factor = paceFactor(pacePercent);
    return {
        leadMs: Math.round(REVEAL_PAYOFF_LEAD_MS * factor),
        beatMs: Math.round(REVEAL_PAYOFF_BEAT_MS * factor),
    };
}
/**
 * How long the finished reveal waits for the host before moving on by itself.
 * The host paces the reveal; this is only here so an absent one cannot freeze
 * the room.
 */
export const REVEAL_AUTO_ADVANCE_MS = 45_000;
export const ROUND_INTRO_MS = 2_500;
/** Clock pauses for this long per tile as it flips in during letters selection. */
export const TILE_FLIP_MS = 450;
// --- Limits ---------------------------------------------------------------
/**
 * Fifteen used to be this number, and it was not a round number picked for a
 * limit: it is the length of a televised episode, ten letters and four numbers
 * and the conundrum. See `ROUND_SEQUENCE_PRESETS`. The argument was that nothing
 * the show does needs more, so a longer plan could only be somebody's house
 * rule.
 *
 * **That day came, and the answer is that a house rule is not this file's
 * business.** The lobby no longer caps the sequence at all — the add buttons
 * never disable — because a room that wants a marathon is not doing anything
 * wrong, and the thing the old limit protected was taste rather than the
 * server.
 *
 * So this is now a RESOURCE GUARD and nothing else. It is here so a malformed
 * or hostile `set-settings` cannot ask for ten thousand rounds and take the
 * process with it; it is not an opinion about how long a game should be. Two
 * hundred rounds is hours of play nobody will sit through, which is exactly
 * what a guard should be — far past any real use, and still bounded.
 *
 * Do not lower it back toward a "sensible" game length. If a number needs to
 * express taste, it belongs in `ROUND_SEQUENCE_PRESETS`, which is a suggestion,
 * not in the validator, which is a refusal. The estimate under the sequence
 * already tells a room what it has asked for.
 */
export const ROUND_SEQUENCE_MAX = 200;
/**
 * How many seats can be given a colour of their own. Sixteen, because
 * `PLAYER_COLORS` has sixteen entries and no more; `colors.test.ts` pins the
 * pair.
 *
 * **This was `MAX_PLAYERS_CAP` and it is not a cap any more.** The seat limit
 * was sixteen for exactly this reason — the palette ran out, and colour is how
 * the reveal, the running order and the standing say who is who, so two players
 * sharing one was judged worse than four players not getting in. That trade was
 * right when the alternative was turning people away. It is wrong when the
 * alternative is a streamer playing five hundred viewers, and the answer is not
 * a bigger palette (see `COLOR_HUE_SEPARATION`, which is already at the floor)
 * but to stop identifying players by colour once there are too many for it to
 * mean anything. Past this line `colorForSeat` returns null, seats are told
 * apart by name, and `aggregateReveal` has turned the reveal into a screen that
 * was not identifying five hundred people by hue in the first place.
 */
export const COLORED_SEATS = 16;
/**
 * Where a room stops being a room and starts being an audience.
 *
 * One number, read by everything that has to behave differently at scale:
 * `aggregateReveal: 'auto'` switches on here, colours stop here, and the game
 * record compresses here. Seventeen rather than some rounder number because it
 * is `COLORED_SEATS + 1` — the first size at which the old design could not
 * have drawn the room anyway.
 */
export const LARGE_ROOM_FROM = COLORED_SEATS + 1;
/**
 * A bound on `maxPlayers` when a host sets one, and a bound on nothing else.
 *
 * `maxPlayers: null` — no limit — is the default, so this is not the cap the
 * room runs into; it is the largest number the validator will accept in the box,
 * so that a fat finger or a hostile client cannot store `maxPlayers:
 * 1e9` and have every seating check allocate against it. Far past any real room.
 */
export const MAX_PLAYERS_HARD_CAP = 5_000;
export const MAX_SPECTATORS_CAP = 5_000;
/**
 * How many seats one LLM model may hold in a single room.
 *
 * **This is a spend guard, and it is the only one that is a hard rule rather
 * than a budget.** Isaac pays OpenRouter out of pocket; `llm/budget.ts` caps the
 * monthly total and `worthCallingFor` declines calls that cannot land in time,
 * but neither of those stops one lobby seating forty copies of the most
 * expensive model in the catalogue and starting a fifteen-round game. Two of
 * each is deliberate rather than one: **one lobby seating the same model twice
 * is an ordinary thing to do** and `db/subject.ts` is built around it (a stats
 * subject is a set of SEATS, never a set of games, precisely for this case). So
 * the ceiling on AI seats in a room is two times the size of the catalogue, and
 * it moves on its own when a model is added.
 *
 * Heuristic bots are not counted. They cost nothing and are not what this is
 * about.
 */
export const MAX_SEATS_PER_MODEL = 2;
/**
 * How many answer rows an aggregated reveal draws, and how many players each
 * row names before it starts counting them instead.
 *
 * Eight rows is Isaac's own number and it is about reading speed rather than
 * completeness — everything not in the eight is still accounted for, in
 * `otherAnswers` and in the histogram. Two names per row is what fits on a phone
 * beside "and 12 others" without the row wrapping.
 *
 * **`AGGREGATE_NAMES_SHOWN` is also the thing that bounds the payload.** Without
 * it a round's aggregate would carry every seat's id somewhere in it, and the
 * broadcast would grow with the room exactly as the un-aggregated one does —
 * which is the problem this screen exists to solve, so solving it only visually
 * would be missing the point.
 */
export const AGGREGATE_ROWS = 8;
export const AGGREGATE_NAMES_SHOWN = 2;
/**
 * The id the audience seat answers under. See `GameSettings.audienceSeat`.
 *
 * **A reserved id rather than a `ServerPlayer`, deliberately.** The audience is
 * one folded answer per round and nothing else: it has no socket, no reconnect
 * token, no device and nothing to identify. Seating it as a real player would
 * put it in `playerOrder` — so in the chooser rotation, in `everyoneLocked`, in
 * the room-full arithmetic and in every membership action — and every one of
 * those would then need a special case saying "except the audience". Instead its
 * answer is written into `round.submissions` under this id at judging, which is
 * the one place it has to exist for the reveal, the round score and the standing
 * to pick it up for free.
 *
 * It is a `player-`prefixed word rather than a UUID because it appears in the
 * reveal and in the standing where a real id would, and a reader tracing one
 * through a payload should be able to see at a glance that it is not a person.
 * Nothing can collide with it: every real seat id is a `randomUUID` or a
 * `bot-`prefixed one.
 */
export const AUDIENCE_SEAT_ID = 'audience';
/**
 * Settings a host may change while a game is running.
 *
 * Everything else in `GameSettings` is a rule of the game and is fixed at the
 * start, because a room that can retune the clock mid-round can retune it in
 * response to how the round is going. These are not rules of the game: they are
 * the controls a host reaches for when something is going wrong in the room, and
 * the moment they are needed is by definition not the lobby. Chat off, the room
 * locked, players' permissions withdrawn — none of them changes what anybody
 * scores.
 *
 * One list, read by `validateSettingsPatch` and by the safety panel, so the
 * control that is on screen and the change the server will accept cannot drift.
 */
export const SAFETY_SETTINGS = [
    'chatMode',
    // One key where there were three. `roomLock` carries what `roomLocked` and
    // `allowSpectators` used to say between them, and `hideRoomCode` is gone —
    // it hid the code from the screen while leaving it in the address bar, which
    // is not a safety control, it is a curtain in front of an open window.
    'roomLock',
    'playerPermissions',
    'allowTeamSelfSelect',
    'visibility',
];
export const CHAT_HISTORY_LIMIT = 200;
export const LOG_HISTORY_LIMIT = 300;
export const APPLIED_ACTION_ID_LIMIT = 500;
export const DISPLAY_NAME_MIN = 1;
export const DISPLAY_NAME_MAX = 24;
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 16;
export const USERNAME_CHANGE_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
export const CHAT_MESSAGE_MAX = 300;
/** IP addresses are dropped after this long. Kept only for ban enforcement. */
export const IP_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const RESERVED_USERNAMES = [
    'guest',
    'admin',
    'moderator',
    'mod',
    'host',
    'system',
    'countdown',
    'dictionary',
    // The folded audience answers under `AUDIENCE_SEAT_ID` and is drawn in the
    // standing beside real people. A player able to register as `audience` could
    // be mistaken for it on every screen it appears on.
    'audience',
    'spectator',
    'everyone',
    'server',
    'staff',
    'support',
    'official',
];
/**
 * Names nobody may WEAR, as opposed to names nobody may register.
 *
 * **Two lists rather than one, because they are asked at different moments.**
 * A username is permanent, unique and typed once; a display name is free text,
 * changeable, and duplicated by suffix rather than refused. The overlap is real
 * but the shapes are not, and a username of `mod` is a different problem from a
 * display name of `Moderator` — the first is squatting, the second is a person
 * standing in the running order claiming to be the room's authority.
 *
 * **What earns a place here is impersonation, not rudeness.** The slur filter
 * is `nameFilter.ts`, it matches on token boundaries, and it is applied only in
 * public rooms because a private room among friends is theirs. This list is the
 * opposite on both counts: it is about claiming to be part of the FURNITURE —
 * the room's system voice, its host, its moderation, or the audience seat that
 * competes beside real people — and that is a lie in a private room as much as
 * a public one, so it is checked inside `validateDisplayName` where every door
 * already goes through it.
 *
 * Matched against a folded form, so `The Audience`, `audience`, `AUDIENCE!!` and
 * `the  audience` are all one name. See `isReservedDisplayName`, which is the
 * only reader and which documents the folding. Entries here are written already
 * folded — lowercase, single-spaced, no leading `the`.
 */
export const RESERVED_DISPLAY_NAMES = [
    'audience',
    'spectator',
    'spectators',
    'host',
    'system',
    'admin',
    'administrator',
    'moderator',
    'mod',
    'server',
    'countdown',
    'dictionary corner',
    'everyone',
    'staff',
    'support',
    'official',
];
// --- Dictionary -----------------------------------------------------------
/**
 * SCOWL size band. Start here and adjust after playing a few games.
 *
 * **80 since 2026-08-02, up from 70, and the games are what moved it.** The
 * reports that decided it were three words a room had ruled out: DOGVANES and
 * UNWIGGED, both real and both sitting at band 80, and DONGLEAVES, which is not
 * a word at any band. Two rejections out of three were the room's own setting
 * refusing ordinary English, and being told `not in the dictionary` about a word
 * that is in the dictionary is the worst thing this number can do.
 *
 * It does NOT let leaked proper nouns in with them. The build script prunes
 * those above band 70 regardless of what a room allows — see `LEAK_PRUNE_ABOVE`
 * in `packages/backend/scripts/build-dictionary.ts`, which was the same constant
 * as this one until this moved and had to stop being.
 */
export const DEFAULT_DICTIONARY_SIZE = 80;
export const DICTIONARY_SIZES = [35, 40, 50, 55, 60, 70, 80, 95];
// --- Defaults -------------------------------------------------------------
/** Three letters, one numbers, one conundrum. The `Short` preset. */
export const SHORT_ROUND_SEQUENCE = [
    'letters',
    'letters',
    'letters',
    'numbers',
    'conundrum',
];
/**
 * The pre-2001 half-hour format: nine rounds in two halves, conundrum last.
 *
 * **The default since 2026-08-05, up from the five-round Short.** Five rounds is
 * a demonstration of the game rather than a game — one numbers round and one
 * conundrum means a single bad sum or a single missed buzz is most of the
 * result, and the standing has no time to move. Nine is the shortest shape with
 * two of each scoring kind in it, so a round lost is a round to recover from.
 *
 * `Full show` is still there for anybody who wants the whole broadcast, and
 * `Short` is still there for a quick one. This is only which of the three a host
 * gets without choosing.
 */
export const CLASSIC_ROUND_SEQUENCE = [
    'letters',
    'letters',
    'letters',
    'numbers',
    'letters',
    'letters',
    'letters',
    'numbers',
    'conundrum',
];
export const DEFAULT_ROUND_SEQUENCE = CLASSIC_ROUND_SEQUENCE;
/**
 * Round plans the lobby offers as one press.
 *
 * Two of the three are the show's own, not a shape invented here, and the
 * order matters as much as the counts — a game that plays its four numbers
 * rounds back to back is not the programme however many of each it has.
 *
 * `classic` is the original half-hour format, nine rounds in two halves of
 * three letters and one numbers, with the conundrum closing the second half.
 * `full` is the episode as broadcast since September 2001: fifteen rounds in
 * three sections, ten letters, four numbers and the conundrum last —
 *
 *     LLN | LLN LLN | LLLL N C
 *
 * Sources: the Wikipedia article on Countdown (game show), section on format,
 * which gives both the fifteen-round layout section by section and the
 * pre-2001 nine-round one; cross-checked against the Channel 4 Countdown wiki.
 * Both were read on 2026-07-31 and agree on the counts and the order.
 *
 * A preset is the plan and nothing else. It deliberately does not carry the
 * clocks: a host who has set their own round lengths and then tries a longer
 * shape should not lose them.
 */
export const ROUND_SEQUENCE_PRESETS = [
    { id: 'short', label: 'Short', sequence: SHORT_ROUND_SEQUENCE },
    { id: 'classic', label: 'Medium', sequence: CLASSIC_ROUND_SEQUENCE },
    {
        id: 'full',
        label: 'Full show',
        sequence: [
            // section one
            'letters',
            'letters',
            'numbers',
            // section two, either side of the Dictionary Corner anecdote
            'letters',
            'letters',
            'numbers',
            'letters',
            'letters',
            'numbers',
            // section three, either side of Origins of Words
            'letters',
            'letters',
            'letters',
            'letters',
            'numbers',
            'conundrum',
        ],
    },
];
export const DEFAULT_SETTINGS = {
    roundSequence: [...DEFAULT_ROUND_SEQUENCE],
    lettersSelectionSeconds: 30,
    lettersPlaySeconds: 30,
    numbersSelectionSeconds: 15,
    // Longer than the letters round on purpose, and the one clock that is not 30.
    // A letters round is recognition — you either see a seven or you do not — and
    // thirty seconds is the show's own answer to it. Six tiles and a target is
    // arithmetic, done by hand, and the same thirty seconds had rooms locking in
    // an unfinished sum. The three play clocks therefore disagree out of the box,
    // which is why the lobby shows three controls rather than one.
    numbersPlaySeconds: 45,
    conundrumSeconds: 30,
    conundrumBuzzAnswerSeconds: 10,
    conundrumLength: 9,
    // No limit. The old default was 8 against a ceiling of 16, and both were
    // really the palette talking; see `COLORED_SEATS`. A room that wants a limit
    // sets one under advanced settings, and a room that wants to stop letting
    // people in reaches for `roomLocked`, which is the control that actually
    // matches what anybody wanted the cap for.
    maxPlayers: null,
    // No limit, matching `maxPlayers`. It was 500, which is not a number anybody
    // chose — it was the old hard cap wearing a different hat, and a room that
    // hits it is a room doing well. `roomLock` is what a host reaches for when
    // they want the door shut, and it says so.
    maxSpectators: null,
    roomLock: 'open',
    visibility: 'private',
    playerPermissions: false,
    allowTeamSelfSelect: true,
    pauseOnDisconnect: false,
    dictionarySize: DEFAULT_DICTIONARY_SIZE,
    allowSpectatorPlayAlong: true,
    lettersWordCheck: true,
    llmBuzzMode: 'human-like',
    llmPrefersRealWords: true,
    revealPacePercent: REVEAL_PACE_DEFAULT,
    hostRulings: false,
    chatMode: 'everyone',
    aggregateReveal: 'auto',
    conundrumBuzzer: true,
    audienceSeat: false,
    audienceAggregation: 'median',
    audienceCanWin: true,
};
/**
 * Whether a room of this many seats draws itself as a room or as an audience.
 *
 * The one reader of `aggregateReveal`, so the reveal, the record page and the
 * lobby's own preview cannot disagree about which screen a game is getting.
 */
export function revealIsAggregated(settings, seatCount) {
    if (settings.aggregateReveal === 'on')
        return true;
    if (settings.aggregateReveal === 'off')
        return false;
    return seatCount >= LARGE_ROOM_FROM;
}
/**
 * The colour for the nth seat, or null once there are more seats than colours.
 *
 * **Null is a real answer and every caller has to draw it**, rather than falling
 * back to a grey that is itself a colour and reads as an identity. Past
 * `COLORED_SEATS` nobody in the room has a colour — not "the first sixteen keep
 * theirs and the rest go grey", which would be sixteen people marked out of five
 * hundred for no reason a player could name. It is all or nothing, decided by
 * how many seats there are.
 *
 * The index is a position in `playerOrder`, so it is stable for the length of a
 * lobby and shifts when somebody leaves. That is the same behaviour as before.
 */
export function colorForSeat(index, seatCount) {
    if (seatCount > COLORED_SEATS)
        return null;
    return PLAYER_COLORS[index % PLAYER_COLORS.length] ?? null;
}
/**
 * Whether a game played under these settings counts towards anybody's stats.
 *
 * One rule, one place, read by `persistGame` and by `toClientState` so the
 * banner in the room and the row on the disk can never disagree.
 *
 * **Host rulings and statistics do not mix.** A host having a bad game can allow
 * themselves a word or strike somebody else's, and any rule that keeps part of
 * the game leaves that part worth rigging for. Isaac got here himself and the
 * sentence is worth keeping: "I do slightly worry it can be used to
 * intentionally rig the results — like if a player does bad, they intentionally
 * override rulings."
 *
 * **This used to throw the whole game away and it no longer does.** The old rule
 * skipped the entire `recordGame` write: no page, no scoreboard, no words, for
 * every game in a room that had the setting on. That protected the averages by
 * destroying the afternoon — and the page is not the averages. The game is
 * written now, marked, and excluded from every aggregate: `games.ranked` is 0,
 * the leaderboard and the bests and the model pages and the profile
 * distributions all filter on it, and it is not a game played.
 *
 * It remains a property of the SETTING, not of whether a ruling was actually
 * made. A room that turned the feature on and never used it is still unranked,
 * because the alternative is a room that can find out mid-game whether its
 * statistics are still live and decide accordingly.
 */
export function gameCountsForStats(settings) {
    return settings.hostRulings !== true;
}
/**
 * Cap on stored LLM reasoning, in characters.
 *
 * Reasoning is broadcast inside round state once the reveal starts, to every
 * client, in every subsequent state payload. A reasoning model working on an
 * anagram will produce several thousand tokens of it without trying, which
 * would make the thinking larger than the entire rest of the game state. Two
 * thousand characters is enough to read the shape of the attempt, which is the
 * whole reason anyone wants to see it.
 */
export const REASONING_MAX_CHARS = 2000;
/**
 * Whether a seat may ask its model for native reasoning tokens. **Off.**
 *
 * Native reasoning costs about ten times a plain answer, and this whole game is
 * one one-word answer per round — so it is the single most expensive thing the
 * room can switch on, for a feature that is a curiosity rather than part of
 * playing. Isaac pays for OpenRouter out of pocket and the table was brought
 * from 72 cents a game to about 3; a room that ticked this on for eight seats
 * would undo that on its own.
 *
 * **It is a kill switch, not a deletion.** Every other part of the feature is
 * still wired: `ClientSubmission.reasoning` still carries what was captured,
 * `ReasoningNote` still draws the chip, and `ReasoningModal` still opens. Games
 * recorded while the control existed still show what their models were thinking,
 * because that reasoning was public when it was written and hiding it now would
 * edit the record rather than stop the spending. Flip this to `true` and the
 * lobby control comes back with it.
 *
 * **The guarantee is here rather than in the lobby**, and that is the point of
 * the flag. Removing the control makes it unreachable from OUR client; this
 * makes it unreachable from ANY client, because the three engine paths that
 * could set `botReasoning` — `addBot`, `setBotModel`, `setBotKind` — all read it
 * before they store. A caller that sends `reasoning: true` is not refused, it is
 * simply stored as false, so an old client with the control still on screen
 * seats an ordinary opponent rather than an error.
 */
export const REASONING_AVAILABLE = false;
/**
 * What `botReasoning` is allowed to become, given what a caller asked for.
 *
 * One implementation, read by all three engine paths, so a fourth way to seat a
 * bot cannot quietly miss the switch.
 */
export function reasoningAllowed(requested) {
    return REASONING_AVAILABLE && requested;
}
// --- Player and team colors ----------------------------------------------
//
// These are DATA rather than CSS tokens, so `packages/frontend/src/palette.test.ts`
// — which parses the stylesheet — cannot see them, and they shipped for a while
// failing the light theme badly: the old team yellow reached 1.7:1 against the
// light background and the old team red 3.3:1. `./colors.test.ts` measures these
// instead, so an edit that breaks the contract fails rather than ships.
//
// Two rules, and one of them is a compromise worth understanding.
//
// FILL ONLY, at 3:1. Every colour here clears 3:1 — the WCAG 1.4.11 bar for a
// graphical object — against BOTH themes' background, surface and raised
// surface. It is a swatch, a border, a dot, a wash: never a text colour. Text
// drawn on top of one uses `ink-on-fill`, exactly as CLAUDE.md says for the
// other mid-tone fills.
//
// 4.5:1 in both themes is not merely unmet here, it is unreachable. The light
// background sits at relative luminance 0.927 and the dark one at 0.003, so a
// single value would have to be darker than 0.172 and lighter than 0.265 at the
// same time. A search over the whole sRGB gamut puts the ceiling at 3.91:1
// (a mid green at oklch(0.555 0.13 125)). Text-grade colour would need one value
// per theme, which means CSS tokens rather than data on a Team.
//
// DISJOINT, so a colour identifies one thing. Teams own colour outright: while
// teams are on, a player renders in their team's colour, so an unteamed player
// wearing something close to a team's colour is a player nobody can place. Every
// player hue stays at least COLOR_HUE_SEPARATION degrees clear of every team
// hue, and the test also measures perceptual distance in OKLab, because two
// colours can differ in hue and still look the same.
/**
 * Minimum hue separation, in degrees, between a player colour and a team one.
 *
 * **Lowered from 25 to 21 on 2026-07-31, deliberately, to fit sixteen players.**
 *
 * The four team hues (25, 90, 155, 250) cut the wheel into four arcs, and each
 * arc loses 2 x this from its ends, so the hue available to players is
 * 360 - 8 x SEPARATION. At 25 that is 160 degrees for sixteen colours, and the
 * only way to fit them is to stop using hue and start using chroma — which
 * lands you with ten violets and four near-greys that clear the OKLab floor on
 * paper and are unusable on a screen. A search over the whole feasible space
 * puts the wall at 22: at 22 and above the best sixteen-colour palette tops out
 * around 0.041 apart, under the 0.05 floor. At 21 it reaches 0.054.
 *
 * Twenty-one is still legible because the two constraints that actually do the
 * work were left alone. Every player colour is still at least 0.07 from every
 * team colour in OKLab — a bigger bar than the 0.05 between two players, since
 * mistaking yourself for a side is worse than mistaking two individuals — and
 * that bar is what a hue separation is a cheap proxy for. And the two are never
 * on screen competing: with teams on, a player renders in their team's colour,
 * so a player colour and a team colour are not two things to tell apart at
 * once. What 21 buys is a full room where nobody wears somebody else's colour,
 * which is the confusion that actually happened.
 */
export const COLOR_HUE_SEPARATION = 21;
/** The floor every palette colour clears on every surface, in both themes. */
export const COLOR_CONTRAST_FLOOR = 3;
/**
 * Team colours: red, blue, gold and green, as far apart as the contrast floor
 * allows. The gold is a deep one — a bright yellow cannot clear 3:1 on a white
 * background, which is why the old one failed at 1.7:1 — so it is named for what
 * it renders as.
 */
export const TEAM_COLORS = [
    { id: 'red', name: 'Red', color: 'oklch(0.605 0.245 25)' },
    { id: 'blue', name: 'Blue', color: 'oklch(0.585 0.165 250)' },
    { id: 'yellow', name: 'Gold', color: 'oklch(0.565 0.115 90)' },
    { id: 'green', name: 'Green', color: 'oklch(0.565 0.14 155)' },
];
/**
 * Individual colours, for players on no team. **Sixteen**, one per seat at
 * `MAX_PLAYERS_CAP`, so a full room never repeats one.
 *
 * These are solved, not chosen, and re-solving them is a search rather than an
 * edit. The constraints are the ones `./colors.test.ts` measures: 3:1 against
 * six surfaces, 0.05 apart from each other in OKLab, 0.07 from every team
 * colour, and COLOR_HUE_SEPARATION degrees of hue from every team hue. The
 * result of that search is what is written here, in hue order.
 *
 * Two things about the shape of the answer are worth knowing before anyone
 * tries to make one of them prettier.
 *
 * **They are not evenly spaced, because the gamut is not even.** The 3:1 floor
 * in both themes pins every colour to a mid lightness, and mid-lightness sRGB
 * holds far more chroma at magenta than at cyan: the ceiling is about 0.30
 * around hue 320 and about 0.09 around hue 200. Distance in OKLab is roughly
 * chroma times hue angle, so the violet-to-red half of the wheel carries seven
 * colours at 12-14 degrees apart while the teal-to-blue quarter needs 13-23
 * degrees for four, and those four lean on lightness alternating between about
 * 0.53 and 0.58 for the rest of their separation. Flattening the lightnesses to
 * make the list look tidy collapses that half of the palette.
 *
 * **The chroma is at or near the per-hue ceiling on purpose.** A desaturated
 * colour can satisfy every distance in this file and still read as grey, and a
 * player identified by a grey is a player identified by nothing. Anything much
 * under the ceiling here failed that eye test even while passing the numbers.
 *
 * Roughly, in order: crimson, orange, amber, green, olive, teal, cyan, azure,
 * steel blue, indigo, violet, purple, magenta, fuchsia, pink, rose.
 */
export const PLAYER_COLORS = [
    'oklch(0.57 0.21 3)',
    'oklch(0.56 0.15 47)',
    'oklch(0.6 0.13 62)',
    'oklch(0.55 0.13 122)',
    'oklch(0.54 0.08 134)',
    'oklch(0.53 0.09 176)',
    'oklch(0.58 0.08 199)',
    'oklch(0.53 0.09 212)',
    'oklch(0.58 0.11 228)',
    'oklch(0.56 0.19 271)',
    'oklch(0.61 0.22 285)',
    'oklch(0.57 0.23 297)',
    'oklch(0.58 0.25 311)',
    'oklch(0.61 0.23 323)',
    'oklch(0.59 0.25 337)',
    'oklch(0.61 0.25 350)',
];
